import type { Navigator, Screen } from "../app/screen";
import { Api, ApiError } from "../net/api";
import { pageShell, note } from "../ui/page";
import { setChoice } from "../app/session";
import { loadCardNames, cardName } from "../match/cards";
import { audio } from "../fx/audio";

/**
 * PRACTICE - a match that costs nothing, against an opponent you choose.
 *
 * ⭐ **ITS OWN DOOR, BECAUSE A NEW PLAYER WILL NOT GO LOOKING UNDER "QUICK MATCH".** Alex,
 * 2026-08-15: *"I would prefer the Practice is a different button below Duels. That way it is very
 * clear for new users instead of trying to find it under the Play - Quick Match."*
 *
 * He is right, and the first attempt put the opponent-skill choice on the PLAY screen as a property
 * of a quick match - which is the correct SHAPE and the wrong PLACE. Somebody who has just built
 * their first deck and wants to try it is not going to read the ladder screen looking for a
 * difficulty toggle.
 *
 * ⭐ **AND SEPARATING THEM MAKES BOTH HONEST.** PLAY is the ladder: a league, and whatever opponent
 * the ladder gives you. PRACTICE is the sandbox: your deck, your choice of opposition, and nothing
 * recorded either way. Leaving the skill toggle on PLAY blurred the two into one screen that was
 * quietly both.
 *
 * ⚠️ **NOTHING IS RECORDED HERE, AND IT SAYS SO.** A player testing a deck against Hard should not
 * be watching their record; a player who thinks it counts will avoid the mode that exists to be
 * risk-free.
 */

type Skill = "BankedFire" | "Normal" | "Hard";

/**
 * ⭐ **THREE TIERS, AND THE GENTLEST ONE IS A HANDICAP THAT ALREADY EXISTED.** Alex, 2026-08-15:
 * *"shall we add one more practice level ... where the player starts with extra foxfire? ... Ideally
 * with an American lore feel and basically it is with a buff or a handicap."*
 *
 * `MatchContext.Newbie` has given the human +2 opening Foxfire since 2026-08-02 and was reachable
 * only through a toggle in the Unity bootstrap - a finished mechanic behind a control the web client
 * never had. This offers it.
 *
 * ⭐ **"BANKED FIRE" NAMES THE CONDITION, NOT THE PLAYER.** A banked fire is one kept alive under
 * ash overnight so it can be woken in the morning - ordinary North American frontier practice, and
 * it is literally what the tier does: you start with the fire already going. "Newbie", "Greenhorn"
 * and "Tenderfoot" all label the PERSON, which is the thing a nervous new player is most likely to
 * bounce off. (Greenhorn was the runner-up and is the one to switch to if this reads as too oblique.)
 */
const SKILLS: ReadonlyArray<readonly [Skill, string, string]> = [
  ["BankedFire", "BANKED FIRE",
   "You open with the fire already going - extra Foxfire on your first turn. The gentlest way in."],
  ["Normal", "NORMAL", "Plays the board straight. Good for learning a deck."],
  ["Hard", "HARD", "Trades better and punishes a slow curve. Good for testing one."],
];

export const PracticeScreen: Screen = {
  id: "practice",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "PRACTICE", "no stakes, your choice of opponent");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "practice";
    shell.body.append(root);

    // ── the mode ─────────────────────────────────────────────────────────────────────────────────
    //
    // ⭐ **THREE WAYS TO PRACTISE, AND TWO OF THEM COST ALMOST NO NEW GAME LOGIC.**
    //
    //   DUEL    a normal match against a bot at a chosen skill - what this screen already was.
    //   PUZZLE  one of the authored fixed positions. Every one is PROVEN to have exactly one
    //           solution by `POST /api/v1/Puzzle/Solve`, which plays every line against the real
    //           engine - so a puzzle offered here cannot be the unwinnable kind.
    //   SPECIAL a normal match under one persistent rule. `ApplySpecialRule` already runs whatever
    //           the context names, every turn, in any mode; it was only ever reached from a campaign
    //           stage. The whole mode is a field on the context.
    //
    // ⚠️ **A PUZZLE IS NOT PROGRESSION AND IS NOT GATED.** Practice exists for the player who has not
    // cleared the campaign, so locking a practice puzzle behind the stage it lives on would deny it
    // to exactly the person it is for.
    type Mode = "duel" | "puzzle" | "special";
    let mode: Mode = "duel";

    const modeHead = document.createElement("h2");
    modeHead.className = "practice__head";
    modeHead.textContent = "WHAT TO PRACTISE";

    const modeRow = document.createElement("div");
    modeRow.className = "practice__skills";
    const modeBtns: HTMLButtonElement[] = [];
    const MODES: Array<[Mode, string, string]> = [
      ["duel", "DUEL", "a full match against a bot"],
      ["puzzle", "PUZZLE", "one fixed position, one answer"],
      ["special", "SPECIAL", "a match under one strange rule"],
    ];
    for (const [value, label, blurb] of MODES) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "practice__skill" + (value === mode ? " practice__skill--on" : "");
      b.dataset["label"] = label;
      const t = document.createElement("span");
      t.className = "practice__skilltitle";
      t.textContent = label;
      const sub = document.createElement("span");
      sub.className = "practice__skillsub";
      sub.textContent = blurb;
      b.append(t, sub);
      b.addEventListener("click", () => {
        mode = value;
        for (const other of modeBtns) other.classList.toggle("practice__skill--on", other === b);
        audio.play("click", 0.5);
        applyMode();
      });
      modeBtns.push(b);
      modeRow.append(b);
    }

    // ── the opponent ─────────────────────────────────────────────────────────────────────────────
    let skill: Skill = "Normal";
    const oppHead = document.createElement("h2");
    oppHead.className = "practice__head";
    oppHead.textContent = "THE OPPONENT";

    const skillRow = document.createElement("div");
    skillRow.className = "practice__skills";
    const skillBtns: HTMLButtonElement[] = [];
    for (const [value, label, blurb] of SKILLS) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "practice__skill" + (value === skill ? " practice__skill--on" : "");
      b.dataset["label"] = label;
      const t = document.createElement("span");
      t.className = "practice__skilltitle";
      t.textContent = label;
      const s = document.createElement("span");
      s.className = "practice__skillsub";
      s.textContent = blurb;
      b.append(t, s);
      b.addEventListener("click", () => {
        skill = value;
        for (const other of skillBtns) other.classList.toggle("practice__skill--on", other === b);
        audio.play("click", 0.5);
      });
      skillBtns.push(b);
      skillRow.append(b);
    }

    // ── the deck ─────────────────────────────────────────────────────────────────────────────────
    const deckHead = document.createElement("h2");
    deckHead.className = "practice__head practice__head--row";
    const deckHeadText = document.createElement("span");
    deckHeadText.textContent = "YOUR DECK";
    // The same door as the ladder screen: a player deciding what to practise WITH may want a new one.
    const buildLink = document.createElement("button");
    buildLink.type = "button";
    buildLink.className = "play__build";
    buildLink.dataset["label"] = "BUILD A DECK";
    buildLink.textContent = "BUILD A DECK";
    buildLink.addEventListener("click", () => { audio.play("click", 0.6); nav.go("decks"); });
    deckHead.append(deckHeadText, buildLink);
    const deckList = document.createElement("div");
    deckList.className = "practice__decks";
    deckList.append(note("Reading your decks..."));

    const foot = document.createElement("div");
    foot.className = "practice__foot";
    const hint = note("Choose a deck.");
    const go = document.createElement("button");
    go.type = "button";
    go.className = "plate practice__go";
    go.dataset["label"] = "PLAY";
    const face = document.createElement("span");
    face.className = "plate__face";
    face.setAttribute("aria-hidden", "true");
    const label = document.createElement("span");
    label.className = "plate__label";
    label.textContent = "PLAY";
    go.append(face, label);
    foot.append(hint, go);

    // The list of authored puzzles, shown only in puzzle mode.
    const puzzleHead = document.createElement("h2");
    puzzleHead.className = "practice__head";
    puzzleHead.textContent = "THE POSITION";
    const puzzleList = document.createElement("div");
    puzzleList.className = "practice__skills practice__puzzles";
    let puzzlePick: { quadrant: string; stageIndex: number; isHeroic: boolean } | null = null;

    // The special rules the SERVER implements. Fetched, never listed here - a hard-coded rule the
    // server does not run would advertise a mode that plays as an ordinary game.
    const ruleHead = document.createElement("h2");
    ruleHead.className = "practice__head";
    ruleHead.textContent = "THE RULE";
    const ruleList = document.createElement("div");
    ruleList.className = "practice__skills";
    let rulePick: string | null = null;

    root.append(modeHead, modeRow, oppHead, skillRow, puzzleHead, puzzleList,
                ruleHead, ruleList, deckHead, deckList, foot);

    /** Show only what the chosen mode needs. */
    function applyMode(): void {
      const isPuzzle = mode === "puzzle";
      const isSpecial = mode === "special";
      // ⚠️ A PUZZLE SUPPLIES ITS OWN DECK AND ITS OWN OPPONENT - the position IS the match. Offering
      // a deck picker beside it would be a control that changes nothing, which is the class of thing
      // this rebuild exists to delete.
      oppHead.hidden = isPuzzle;
      skillRow.hidden = isPuzzle;
      deckHead.hidden = isPuzzle;
      deckList.hidden = isPuzzle;
      puzzleHead.hidden = !isPuzzle;
      puzzleList.hidden = !isPuzzle;
      ruleHead.hidden = !isSpecial;
      ruleList.hidden = !isSpecial;
      refresh();
    }

    let deckId: string | undefined;
    let deckName: string | undefined;

    function refresh(): void {
      // ⚠️ Each mode has a DIFFERENT precondition, and a PLAY button that is live before its mode is
      // ready sends a request that fails for a reason the player cannot see.
      const ready = mode === "puzzle" ? puzzlePick !== null
                  : mode === "special" ? (rulePick !== null && !!deckId)
                  : !!deckId;
      go.toggleAttribute("disabled", !ready);
      hint.textContent = ready
        ? "Nothing here is recorded - win or lose, your record is untouched."
        : mode === "puzzle" ? "Choose a position."
        : mode === "special" ? "Choose a rule and a deck."
        : "Choose a deck.";
    }
    refresh();

    go.addEventListener("click", () => {
      audio.play("queue");

      if (mode === "puzzle") {
        if (!puzzlePick) return;
        // ⭐ A puzzle IS a campaign stage - it supplies its own deck, its own opponent and its own
        // position. Starting it through the same route the campaign uses means practice and the
        // campaign can never deal a different board for the same puzzle.
        const pick = puzzlePick;
        void Api.campaignStartStage({
          quadrant: pick.quadrant, stageIndex: pick.stageIndex, isHeroic: pick.isHeroic,
        }).then(() => nav.go("match"))
          .catch((e) => { hint.textContent = `Could not start it: ${e instanceof Error ? e.message : String(e)}`; });
        return;
      }

      if (!deckId) return;
      // ⚠️ NO leagueId. Practice is not on the ladder, and sending one would file the result.
      setChoice({
        deckId, deckName, difficulty: skill,
        // ⚠️ Cleared when not in special mode. Leaving a stale rule on the shared choice would make
        // the NEXT ordinary practice match silently play under it.
        specialRule: mode === "special" && rulePick ? rulePick : undefined,
      });
      nav.go("match");
    });

    // ── fill the puzzle and rule lists ───────────────────────────────────────────────────────────
    //
    // ⚠️ Both come FROM THE SERVER. A hard-coded puzzle list drifts the first time one is authored,
    // and a hard-coded rule list can name one the server does not implement - which plays as an
    // ordinary match while claiming to be a mode.
    void Api.puzzleList()
      .then((r) => {
        puzzleList.replaceChildren();
        if (!r.puzzles.length) {
          puzzleList.append(note("No puzzles are authored yet.", true));
          return;
        }
        for (const pz of r.puzzles) {
          const b = document.createElement("button");
          b.type = "button";
          b.className = "practice__skill";
          b.dataset["label"] = pz.title;
          const t = document.createElement("span");
          t.className = "practice__skilltitle";
          t.textContent = pz.title;
          const sub = document.createElement("span");
          sub.className = "practice__skillsub";
          // The OBJECTIVE, not the realm: it is what the player has to do, and it is the only thing
          // that distinguishes one position from another before they open it.
          sub.textContent = pz.objective === "BoardClear" ? "clear their board" : "kill them this turn";
          b.append(t, sub);
          b.addEventListener("click", () => {
            puzzlePick = { quadrant: pz.quadrant, stageIndex: pz.stageIndex, isHeroic: pz.isHeroic };
            for (const other of puzzleList.querySelectorAll("button")) {
              other.classList.toggle("practice__skill--on", other === b);
            }
            audio.play("click", 0.5);
            refresh();
          });
          puzzleList.append(b);
        }
      })
      .catch(() => {
        puzzleList.replaceChildren(note("Could not read the puzzle list.", true));
      });

    // ⭐ The rules the SERVER implements, in the player's words. The KEY is the engine's.
    const RULES: Array<[string, string, string]> = [
      ["TheLongDark", "THE LONG DARK", "both heroes lose 1 health a round"],
      ["CanopyRally", "CANOPY RALLY", "both players draw an extra card a round"],
      ["TheSecondDeath", "THE SECOND DEATH", "every creature is silenced each round"],
    ];
    for (const [key, label, blurb] of RULES) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "practice__skill";
      b.dataset["label"] = label;
      const t = document.createElement("span");
      t.className = "practice__skilltitle";
      t.textContent = label;
      const sub = document.createElement("span");
      sub.className = "practice__skillsub";
      sub.textContent = blurb;
      b.append(t, sub);
      b.addEventListener("click", () => {
        rulePick = key;
        for (const other of ruleList.querySelectorAll("button")) {
          other.classList.toggle("practice__skill--on", other === b);
        }
        audio.play("click", 0.5);
        refresh();
      });
      ruleList.append(b);
    }

    applyMode();

    await loadCardNames();
    try {
      const decks = await Api.decks() as Array<{ id?: string; name?: string; heroId?: string }>;
      deckList.replaceChildren();
      if (!decks.length) {
        deckList.append(note("You have no decks yet. Build one from DECKS on the menu.", true));
        return;
      }
      for (const d of decks) {
        if (!d.id) continue;
        const row = document.createElement("button");
        row.type = "button";
        row.className = "practice__deck";
        row.dataset["label"] = d.name ?? "DECK";
        const t = document.createElement("span");
        t.className = "practice__decktitle";
        t.textContent = d.name ?? "Deck";
        const s = document.createElement("span");
        s.className = "practice__decksub";
        s.textContent = d.heroId ? cardName(d.heroId) : "";
        row.append(t, s);
        row.addEventListener("click", () => {
          deckId = d.id;
          deckName = d.name;
          for (const other of Array.from(deckList.children)) {
            other.classList.remove("practice__deck--on");
          }
          row.classList.add("practice__deck--on");
          audio.play("click", 0.5);
          refresh();
        });
        deckList.append(row);
      }
    } catch (e) {
      deckList.replaceChildren(note(e instanceof ApiError ? e.body || e.message : String(e), true));
    }
  },
};
